// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PanelMenu from "@insite/mobius/PanelMenu/PanelMenu";
import PanelRow from "@insite/mobius/PanelMenu/PanelRow";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import MiniMyAccount from "@west/App/Components/MiniMyAccount/MiniMyAccount";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import { RowWrapper } from "@west/MobiusOverrides/PanelMenu";
import { NavigationDrawerStyles } from "@west/Overrides/Components/NavigationDrawer";
import { OwnProps as HeaderSignInProps } from "@west/Overrides/Widgets/Common/HeaderSignIn";
import { myAccountMapLinks } from "@west/XDL/Utilities/links";
import React, { FC } from "react";

interface OwnProps {
    styles: NavigationDrawerStyles;
    currentPageUrl?: string;
    closeDrawer: () => void;
}

const HeaderSignInNavigationDrawer: FC<OwnProps> = ({ styles, closeDrawer, currentPageUrl }) => {
    const { isAuthenticated, myAccountLinksMapped, myAccountPageUrl, signInPageUrl } = useAppSelector(state => {
        const myAccountLinksField = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Common/HeaderSignIn";
        })?.fields as HeaderSignInProps["fields"];

        return {
            isAuthenticated: xIsAuthenticated(state),
            myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
            signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
            myAccountLinksMapped: myAccountMapLinks(state, myAccountLinksField.myAccountLinks),
        };
    });

    const history = useHistory();

    if (!isAuthenticated) {
        return (
            <PanelRow
                className="signIn-row"
                {...styles.mainNavigationRow}
                href={signInPageUrl}
                isCurrent={currentPageUrl === signInPageUrl}
                onClick={closeDrawer}
                onKeyPress={(event: React.KeyboardEvent) => {
                    if (event.key === " " && history && signInPageUrl) {
                        event.preventDefault();
                        closeDrawer();
                        history.push(signInPageUrl);
                    }
                }}
            >
                <Typography className="signIn-title" {...styles.logoLinks?.typographyProps}>
                    {translate("Sign In")}
                </Typography>
            </PanelRow>
        );
    }

    const isCurrentPage = currentPageUrl === myAccountPageUrl;
    let menuItem: JSX.Element | null = (
        <PanelRow
            href={myAccountPageUrl}
            isCurrent={isCurrentPage}
            className="main-navigation-row"
            onClick={closeDrawer}
            {...styles.mainNavigationRow}
            color="common.ui8"
        >
            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                {translate("My Account")}
            </Typography>
        </PanelRow>
    );

    if (isAuthenticated && myAccountLinksMapped.length === 0) {
        return menuItem;
    }

    let menu;
    let panelRow;
    const renderChildren = true;

    if (renderChildren) {
        if (myAccountPageUrl) {
            panelRow = <PanelRow isCurrent={isCurrentPage} hasChildren></PanelRow>;
        } else {
            panelRow = React.cloneElement(menuItem, { hasChildren: true });
            menuItem = null;
        }
        menu = (
            <PanelMenu
                className="panel-menu"
                currentUrl={currentPageUrl}
                closeOverlay={closeDrawer}
                panelTrigger={panelRow}
                layer={0}
                {...styles.panelMenu}
            >
                <MiniMyAccount myAccountLinks={myAccountLinksMapped} />
            </PanelMenu>
        );
    }

    return (
        <RowWrapper isCurrent={isCurrentPage}>
            {menuItem}
            {menu}
        </RowWrapper>
    );
};

export default HeaderSignInNavigationDrawer;
